// SDK UI kit. Styles live in the runtime's base CSS (fl- prefixed classes)
// and are themed with the theme tokens (--accent, --radius…).
import { render, type ComponentChildren, type JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { attachFitter, scheduleFit, textFitter, tileFitter, type Fitter } from './fit.ts';
import { useBackHandler, useRuntime, useWidget } from './hooks.ts';
import { RuntimeContext, WidgetContext } from './context.ts';
import { Icon, type IconName } from './icons.tsx';

export interface TileProps {
  label: string;
  sub?: string;
  /** A built-in icon name (see iconNames) or an image URL. */
  icon?: IconName | string;
  color?: string;
  /**
   * glass: translucent glass surface that turns white on focus (default without a color) ·
   * solid: colored fill (default with a color) · icon: app icon, logo centered, name below on focus.
   */
  variant?: 'glass' | 'solid' | 'icon';
  /** On/active indicator (e.g. the light is on). */
  active?: boolean;
  /** Shows a busy state (e.g. while an action is running). */
  busy?: boolean;
  /** Tile size outside the grid (e.g. inside a FullScreen). On the launcher the tile fills its cell. */
  size?: 'sm' | 'md' | 'lg';
  /** Give this tile the initial focus when a FullScreen opens. */
  autofocus?: boolean;
  onPress?: () => void;
  children?: ComponentChildren;
}

const isUrl = (s: string) => /[/.:]/.test(s);

function Glyph({ icon }: { icon: string }) {
  return <span class="fl-tile-glyph">{isUrl(icon) ? <img class="fl-tile-img" src={icon} alt="" /> : <Icon name={icon as IconName} class="fl-tile-svg" />}</span>;
}

/**
 * A tile focusable with the remote. On the launcher grid it fills its cell and adapts its layout
 * to the cell's shape: 1x1 icon only, Nx1 horizontal button (icon + text), 2x2 and up a card.
 * Sizes (font, icon, padding) scale with the tile's pixel size, and the label, sub text and focus caption always fit:
 * a long label wraps to 2 lines, then the font shrinks, and only at the minimum size is it cut with an ellipsis.
 */
export function Tile({ label, sub, icon, color, variant, active, busy, size = 'md', autofocus, onPress, children }: TileProps) {
  const v = variant || (color ? 'solid' : 'glass');
  const style = color ? ({ '--tile': color } as JSX.CSSProperties) : undefined;
  const iconOnly = v === 'icon' && icon;
  const ref = useRef<HTMLButtonElement>(null);
  const fitter = useRef<Fitter>();
  const textKey = useRef<string>();
  // Measures the tile (sets --fl-u) and fits its text; refits when the size changes (shape switch, grid change).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const f = (fitter.current = tileFitter(el));
    return attachFitter(f);
  }, []);
  // The text changed: refit before paint (the first fit comes from the size observer above).
  useLayoutEffect(() => {
    const key = `${label}\0${sub || ''}\0${v}\0${icon ? 1 : 0}`;
    if (textKey.current !== undefined && textKey.current !== key && fitter.current) scheduleFit(fitter.current);
    textKey.current = key;
  }, [label, sub, v, icon]);
  return (
    <button
      ref={ref}
      type="button"
      data-focus=""
      data-autofocus={autofocus ? '' : undefined}
      aria-label={label}
      class={`fl-tile fl-tile-${size} fl-tile-${v}${active ? ' is-active' : ''}${busy ? ' is-busy' : ''}`}
      style={style}
      onClick={onPress}
    >
      <span class="fl-tile-face">
        {icon && <Glyph icon={icon} />}
        {children}
        {!iconOnly && (
          <span class="fl-tile-text">
            <span class="fl-tile-label">{label}</span>
            {sub && <small class="fl-tile-sub">{sub}</small>}
          </span>
        )}
      </span>
      {(iconOnly || v !== 'icon') && <span class="fl-tile-caption">{label}</span>}
    </button>
  );
}

/**
 * Text that always fits its box: at most `lines` lines (default 1), the font shrinks (down to `minSize` px) when the text is
 * too long, and it is cut with an ellipsis only at the minimum size. Takes the box width from its parent and re-fits when the width
 * or the text changes. Set the normal font size in CSS (or inherit it); the shrink factor is applied on top of it.
 */
export function FitText({ children, lines = 1, minSize = 12, class: cls }: { children?: ComponentChildren; lines?: number; minSize?: number; class?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const fitter = useRef<Fitter>();
  const last = useRef('');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    fitter.current = textFitter(el, { lines, minPx: minSize });
    last.current = el.textContent || '';
    return attachFitter(fitter.current);
  }, [lines, minSize]);
  // Runs after every render; refits only when the text really changed.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !fitter.current) return;
    const now = el.textContent || '';
    if (now !== last.current) {
      last.current = now;
      scheduleFit(fitter.current);
    }
  });
  return (
    <span ref={ref} class={`fl-fit${cls ? ` ${cls}` : ''}`} style={{ '--fl-lines': lines } as JSX.CSSProperties}>
      {children}
    </span>
  );
}

/** Glass-surface container (for widget cards). Optional title and extra info (meta) on the right. */
export function Card({ title, meta, children, class: cls }: { title?: string; meta?: string; children: ComponentChildren; class?: string }) {
  return (
    <div class={`fl-card${cls ? ` ${cls}` : ''}`}>
      {(title || meta) && (
        <div class="fl-card-head">
          {title && <span class="fl-card-title">{title}</span>}
          {meta && <span class="fl-card-meta">{meta}</span>}
        </div>
      )}
      <div class="fl-card-body">{children}</div>
    </div>
  );
}

/** Horizontally scrolling shelf: as the remote moves left/right, the focused item scrolls into view. */
export function Shelf({ title, meta, children }: { title?: string; meta?: string; children: ComponentChildren }) {
  return (
    <div class="fl-shelf">
      {(title || meta) && (
        <div class="fl-card-head">
          {title && <span class="fl-card-title">{title}</span>}
          {meta && <span class="fl-card-meta">{meta}</span>}
        </div>
      )}
      <div class="fl-shelf-track">{children}</div>
    </div>
  );
}

export interface MediaCardProps {
  title: string;
  sub?: string;
  /** Thumbnail (16:9). An array stacks layers: if the first fails to load, the next one shows. */
  image?: string | string[];
  /** Label in the bottom-right corner (duration, LIVE…). */
  badge?: string;
  /** Watched percentage 0-100: a progress bar under the image. */
  progress?: number;
  /** Card width (px); height is 16:9 plus the text. */
  width?: number;
  /** Title over the image (large featured card). */
  overlay?: boolean;
  autofocus?: boolean;
  onPress?: () => void;
}

/** Video/media card: 16:9 thumbnail, title, subtitle. */
export function MediaCard({ title, sub, image, badge, progress, width = 380, overlay, autofocus, onPress }: MediaCardProps) {
  return (
    <button
      type="button"
      data-focus=""
      data-autofocus={autofocus ? '' : undefined}
      aria-label={title}
      class={`fl-media${overlay ? ' fl-media-overlay' : ''}`}
      style={overlay ? undefined : { width: `${width}px` }}
      onClick={onPress}
    >
      <span class="fl-media-thumb" style={overlay ? undefined : { height: `${Math.round((width * 9) / 16)}px` }}>
        {image && <span class="fl-media-img" style={{ backgroundImage: ([] as string[]).concat(image).map((u) => `url("${u}")`).join(', ') }} />}
        {badge && <span class="fl-media-badge">{badge}</span>}
        {progress != null && progress > 0 && (
          <span class="fl-media-progress">
            <span style={{ width: `${Math.min(100, Math.max(3, progress))}%` }} />
          </span>
        )}
        {overlay && (
          <span class="fl-media-text">
            <span class="fl-media-title">{title}</span>
            {sub && <span class="fl-media-sub">{sub}</span>}
          </span>
        )}
      </span>
      {!overlay && (
        <span class="fl-media-text">
          <span class="fl-media-title">{title}</span>
          {sub && <span class="fl-media-sub">{sub}</span>}
        </span>
      )}
    </button>
  );
}

/** A titled row of tiles (use it when a widget shows several tiles). */
export function Row({ title, children }: { title?: string; children: ComponentChildren }) {
  return (
    <div class="fl-row">
      {title && <div class="fl-row-title">{title}</div>}
      <div class="fl-row-items">{children}</div>
    </div>
  );
}

/** Vertical stack with a gap (px). */
export function Stack({ gap = 8, align, children }: { gap?: number; align?: 'start' | 'end' | 'center'; children: ComponentChildren }) {
  return (
    <div class="fl-stack" style={{ gap: `${gap}px`, alignItems: align === 'end' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
      {children}
    </div>
  );
}

/** Text sized for TV viewing distance. */
export function Text({
  size = 'md',
  tone,
  serif,
  children,
}: {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'display';
  tone?: 'muted' | 'accent';
  serif?: boolean;
  children: ComponentChildren;
}) {
  return <div class={`fl-text fl-text-${size}${tone ? ` fl-${tone}` : ''}${serif ? ' fl-serif' : ''}`}>{children}</div>;
}

function FullScreenLayer({ onClose, title, children, class: cls }: { onClose: () => void; title?: string; children: ComponentChildren; class?: string }) {
  const rt = useRuntime();
  const ref = useRef<HTMLDivElement>(null);
  useBackHandler(() => {
    onClose();
    return true;
  });
  useEffect(() => {
    rt.wake();
    const release = rt.holdAwake();
    // On open: [data-autofocus] (else the first focusable element), so dangerous buttons like "Remove" don't get the first focus.
    const t = setTimeout(() => (ref.current?.querySelector<HTMLElement>('[data-autofocus]') || ref.current?.querySelector<HTMLElement>('[data-focus]'))?.focus(), 60);
    return () => {
      clearTimeout(t);
      release();
    };
  }, []);
  return (
    <div class={`fl-fullscreen${cls ? ` ${cls}` : ''}`} data-focus-scope="" ref={ref}>
      {title && <div class="fl-fullscreen-title">{title}</div>}
      {children}
    </div>
  );
}

type FullScreenProps = { onClose: () => void; title?: string; children: ComponentChildren; class?: string };

/**
 * Full-screen UI (assistant, settings…). The Back key calls onClose, remote focus stays
 * inside, and the TV doesn't switch to gallery mode while it's open. On open it focuses the
 * [data-autofocus] element (e.g. <Tile autofocus>), otherwise the first focusable element.
 */
export function FullScreen(props: FullScreenProps) {
  // The layer is rendered into <body>, not into the widget's DOM: launcher widgets are scaled with a CSS transform, and a transformed ancestor
  // becomes the containing block of `position: fixed`, which would squeeze the layer into the widget's box. The tree is re-provided with the
  // runtime and widget contexts, so hooks inside the layer work as if it were rendered in place.
  const rt = useRuntime();
  const widget = useWidget();
  const host = useRef<HTMLDivElement | null>(null);
  if (!host.current) host.current = document.createElement('div');
  useLayoutEffect(() => {
    const el = host.current!;
    document.body.appendChild(el);
    return () => {
      render(null, el);
      el.remove();
    };
  }, []);
  // Every render of the owner: refresh the layer's content (state of components inside the layer is kept between renders).
  useLayoutEffect(() => {
    render(
      <RuntimeContext.Provider value={rt}>
        <WidgetContext.Provider value={widget}>
          <FullScreenLayer {...props} />
        </WidgetContext.Provider>
      </RuntimeContext.Provider>,
      host.current!,
    );
  });
  return null;
}

/** Render a zone's widgets here: <Zone name="settings" />. */
export function Zone({ name }: { name: string }) {
  return useRuntime().renderZone(name);
}

/** A titled section in full-screen apps like Settings. */
export function Section({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <section class="fl-section">
      <div class="fl-section-title">{title}</div>
      {children}
    </section>
  );
}

export { Icon };
